import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonText,
  IonButton,
} from '@ionic/react';
import {
  calendarOutline,
  locationOutline,
  eyeOutline,
  newspaperOutline,
  timeOutline,
} from 'ionicons/icons';
import { Inspection } from '../../../types/model/inspection';
import { getInspectionStatusColor, getStatusText, getTimeSensitivityColor, getTimeSensitivityText } from '../common/helpers';

interface InspectionCardProps {
  inspection: Inspection;
  onInspectionClick: (inspection: Inspection, event: React.MouseEvent) => void;
}

const InspectionCard: React.FC<InspectionCardProps> = ({
  inspection,
  onInspectionClick,
}) => {

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <IonCard 
      style={{ 
        marginBottom: '1rem', 
        background: 'var(--ion-color-step-50, #ffffff)',
        border: '1px solid var(--ion-color-step-250, var(--ion-color-light-shade))',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        color: 'var(--ion-text-color, inherit)',
      }}
    >
      <IonCardHeader>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
          gap: '16px'
        }}>
          <IonCardTitle style={{ color: 'var(--ion-text-color, inherit)' }}>{inspection.lineItem}</IonCardTitle>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: '0.78rem',
              fontWeight: '600',
              color: getInspectionStatusColor(inspection.status),
              padding: '2px 8px',
              borderRadius: '4px',
              border: `1px solid ${getInspectionStatusColor(inspection.status)}`,
              whiteSpace: 'nowrap',
            }}>
              {getStatusText(inspection.status)}
            </span>
            <IonIcon
              icon={timeOutline}
              style={{ fontSize: '1.1rem', color: getTimeSensitivityColor(inspection.timeSensitivity), flexShrink: 0 }}
              title={getTimeSensitivityText(inspection.timeSensitivity)}
            />
          </div>
        </div>
      </IonCardHeader>
      
      <IonCardContent>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          <IonIcon icon={newspaperOutline} />
          <IonText color="medium">
            <strong>Title</strong>
          </IonText>
          <p style={{ margin: '0', marginLeft: 'auto', textAlign: 'right', flex: '1', color: 'var(--ion-text-color, inherit)' }}>{inspection.title}</p>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          <IonIcon icon={locationOutline} />
          <IonText color="medium">
            <strong>Site</strong>
          </IonText>
          <p style={{ margin: '0', marginLeft: 'auto', textAlign: 'right', flex: '1', color: 'var(--ion-text-color, inherit)' }}>{inspection.siteName}</p>
        </div>

        {inspection.scheduledDate && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem'
          }}>
            <IonIcon icon={calendarOutline} style={{ color: 'var(--ion-color-primary)' }} />
            <IonText color="medium">
              <strong>Scheduled On</strong>
            </IonText>
            <p style={{ margin: '0', marginLeft: 'auto', textAlign: 'right', flex: '1', color: 'var(--ion-color-primary)' }}>{formatDate(inspection.scheduledDate)}</p>
          </div>
        )}
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          <IonIcon icon={calendarOutline} />
          <IonText color="medium">
            <strong>Inspected On</strong>
          </IonText>
          <p style={{ margin: '0', marginLeft: 'auto', textAlign: 'right', flex: '1', color: 'var(--ion-text-color, inherit)' }}>{formatDate(inspection.inspectedOn)}</p>
        </div>

        {/* Action Button */}
        <div style={{ marginTop: '16px' }}>
          <IonButton
            fill="outline"
            size="small"
            onClick={(e) => onInspectionClick(inspection, e)}
          >
            <IonIcon icon={eyeOutline} slot="start" />
            Open Inspection
          </IonButton>
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default InspectionCard;